import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Captures a DOM element and saves it as a multi-page PDF.
 * Each PDF page gets its own canvas slice — no repeated-image artifacts.
 */
export const downloadReportPdf = async (el, filename = "privacy-report") => {
  const scale = 2;

  const fullCanvas = await html2canvas(el, {
    scale,
    useCORS: true,
    allowTaint: false,
    backgroundColor: "#ffffff",
    scrollX: 0,
    scrollY: 0,
    windowWidth:  el.scrollWidth,
    windowHeight: el.scrollHeight,
    logging: false,
  });

  const pdfWidthMm   = 210;   // A4 width mm
  const pageHeightMm = 297;   // A4 height mm

  // How many canvas pixels correspond to one PDF page height?
  const canvasPageHeight = Math.floor(
    (pageHeightMm / pdfWidthMm) * fullCanvas.width
  );

  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const sliceCanvas = document.createElement("canvas");
  sliceCanvas.width = fullCanvas.width;
  const ctx = sliceCanvas.getContext("2d");

  const rootRect = el.getBoundingClientRect();
  const blockBoundaries = Array.from(el.querySelectorAll("[data-pdf-block]"))
    .map((node) => {
      const rect = node.getBoundingClientRect();
      const top = Math.max(0, Math.floor((rect.top - rootRect.top) * scale));
      const bottom = Math.min(fullCanvas.height, Math.ceil((rect.bottom - rootRect.top) * scale));
      return { top, bottom };
    })
    .filter((block) => block.bottom - block.top > 0)
    .sort((a, b) => a.top - b.top);

  const minSliceHeight = Math.floor(canvasPageHeight * 0.55);

  const pickPageEnd = (startY, idealEnd) => {
    if (idealEnd >= fullCanvas.height) return fullCanvas.height;

    const crossingBlock = blockBoundaries.find(
      (block) => block.top < idealEnd && block.bottom > idealEnd
    );

    if (crossingBlock && crossingBlock.top - startY >= minSliceHeight) {
      return crossingBlock.top;
    }

    const candidateBreak = blockBoundaries
      .map((block) => block.top)
      .filter((top) => top > startY + minSliceHeight && top < idealEnd)
      .pop();

    return candidateBreak ?? idealEnd;
  };

  let srcY = 0;
  let pageIndex = 0;

  while (srcY < fullCanvas.height) {
    const idealEnd = Math.min(srcY + canvasPageHeight, fullCanvas.height);
    let pageEnd = pickPageEnd(srcY, idealEnd);

    if (pageEnd <= srcY) pageEnd = idealEnd;

    const srcH = Math.min(pageEnd - srcY, canvasPageHeight);

    // Draw only this page's slice onto a fresh canvas
    sliceCanvas.height = srcH;
    ctx.clearRect(0, 0, sliceCanvas.width, srcH);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, sliceCanvas.width, srcH);
    ctx.drawImage(fullCanvas, 0, srcY, fullCanvas.width, srcH, 0, 0, fullCanvas.width, srcH);

    const sliceData   = sliceCanvas.toDataURL("image/png");
    const sliceHeightMm = (srcH / fullCanvas.width) * pdfWidthMm;

    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceData, "PNG", 0, 0, pdfWidthMm, sliceHeightMm);

    srcY += srcH;
    pageIndex += 1;
  }

  pdf.save(`${filename}.pdf`);
};

